import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Star, Send, Loader2, EyeOff, Heart, RefreshCcw, ShieldAlert, Mic, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { parseLocalDate, formatDate } from '../utils/timeFormat';

// Iter-235e — load the voice recorder lazily so the visual-edits babel
// metadata plugin doesn't try to cross-file-trace it through the typed
// rating flow. Also keeps the rating bundle small for clients (who
// never see the voice path).
const VoiceRecapRecorder = lazy(() => import('./VoiceRecapRecorder'));

/**
 * Iter-163 — Multi-category rating flow.
 *
 * Talent → Assignment/Client (NOT "Rate iEvntür"):
 *   communication, accuracy, professionalism, workload_fairness,
 *   organization, payment_timeliness, overall_experience
 *   + would_work_again toggle + anonymous-to-client toggle
 *   + comment + optional evidence URLs
 *
 * Client → Talent:
 *   punctuality, professionalism, appearance, communication,
 *   teamwork, reliability, overall_performance
 *   + rehire toggle + favorite toggle + private notes (admin only)
 *   + public feedback (visible to talent)
 */
const TALENT_TO_CLIENT_DIMS = [
  ['communication',      'Communication'],
  ['accuracy',           'Assignment Accuracy'],
  ['professionalism',    'Professionalism Onsite'],
  ['workload_fairness',  'Workload Fairness'],
  ['organization',       'Organization'],
  ['payment_timeliness', 'Payment Timeliness'],
  ['overall_experience', 'Overall Experience'],
];

const CLIENT_TO_TALENT_DIMS = [
  ['punctuality',         'Punctuality'],
  ['professionalism',     'Professionalism'],
  ['appearance',          'Appearance / Grooming'],
  ['communication',       'Communication'],
  ['teamwork',            'Teamwork'],
  ['reliability',         'Reliability'],
  ['overall_performance', 'Overall Performance'],
];

const StarRow = ({ value, onChange, label, testId }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={testId}>
    <span className="text-sm text-gray-700 flex-1">{label}</span>
    <div className="flex gap-0.5 shrink-0">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="p-0.5 focus:outline-none"
          aria-label={`${label} ${s} stars`}
          data-testid={`${testId}-star-${s}`}
        >
          <Star
            className={`w-5 h-5 transition-colors ${
              s <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-200'
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

const RatingFlow = ({
  isOpen,
  onClose,
  booking,
  ratingType = 'client_to_talent',
  onRatingSubmitted,
  // Iter-235e — when provided (and ratingType=talent_to_client), render
  // a one-line "Try AI Voice Recap" banner above the typed form. Tapping
  // it switches the dialog body into VoiceRecapRecorder. The banner is
  // suppressed for users who have already completed at least one voice
  // recap (checked via /post-event-recap/voice/status). Set to `false`
  // to forcibly disable the banner (e.g. when caller already exposed
  // the voice flow themselves, like PostEventRecapModal).
  showVoiceBanner = true,
}) => {
  const isClientRatingTalent = ratingType === 'client_to_talent';
  const dims = useMemo(
    () => (isClientRatingTalent ? CLIENT_TO_TALENT_DIMS : TALENT_TO_CLIENT_DIMS),
    [isClientRatingTalent],
  );

  const [scores, setScores] = useState({});
  const [comment, setComment] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [wouldWorkAgain, setWouldWorkAgain] = useState(true);
  const [rehire, setRehire] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Iter-235e — voice nudge banner state.
  // `mode` toggles between the typed flow and the embedded voice recorder.
  // `bannerEligible` is null while we're checking, true if the talent
  // hasn't yet completed a voice recap, false otherwise.
  const [mode, setMode] = useState('typed');
  const [bannerEligible, setBannerEligible] = useState(null);

  const bookingId = booking?.booking_id || booking?.id || null;
  const wantsBanner =
    !!showVoiceBanner && !isClientRatingTalent && !!bookingId;

  useEffect(() => {
    if (!isOpen || !wantsBanner) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/post-event-recap/voice/status');
        if (cancelled) return;
        setBannerEligible(!!r.data?.talent && !r.data?.has_completed_voice);
      } catch (_e) {
        if (!cancelled) setBannerEligible(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, wantsBanner]);

  const overallKey = isClientRatingTalent ? 'overall_performance' : 'overall_experience';
  const overall = scores[overallKey] || 0;
  const filledCount = Object.keys(scores).length;

  const setScore = (key, value) => setScores((s) => ({ ...s, [key]: value }));

  const reset = () => {
    setScores({}); setComment(''); setPrivateNote('');
    setWouldWorkAgain(true); setRehire(true); setIsFavorite(false); setAnonymous(false);
    setMode('typed');
  };

  const handleSubmit = async () => {
    if (overall === 0) {
      toast.error('Please give an overall rating');
      return;
    }
    if (filledCount < 3) {
      toast.error('Please rate at least 3 categories');
      return;
    }
    setSubmitting(true);
    try {
      if (isClientRatingTalent) {
        // Client → Talent
        await api.post('/client/ratings', {
          booking_id: booking.booking_id || booking.id,
          rating: overall,
          private_notes: privateNote || null,
          performance_feedback: comment || null,
          category_scores: scores,
          rehire,
          is_favorite: isFavorite,
        });
      } else {
        // Talent → Client / Assignment
        await api.post('/talent/ratings', {
          booking_id: booking.booking_id || booking.id,
          client_rating: overall,
          client_feedback: comment || null,
          gig_rating: scores.organization || overall, // legacy compat
          gig_feedback: null,
          would_work_again: wouldWorkAgain,
          category_scores: scores,
          is_anonymous_to_client: anonymous,
        });
      }
      toast.success('Thank you — your rating has been submitted.');
      reset();
      onRatingSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { if (!submitting) { reset(); onClose?.(); } };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="rating-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'voice'
              ? 'Voice Recap'
              : isClientRatingTalent ? 'Rate Talent' : 'Rate Assignment & Client'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'voice'
              ? 'Hold the mic and tell us how it went — AI handles the rest.'
              : isClientRatingTalent
                ? `How did ${booking?.name || 'the talent'} perform on this assignment?`
                : `Tell us how this assignment went — your feedback only goes to the client and admin team. Never to the iEvntür platform.`}
          </DialogDescription>
        </DialogHeader>

        {mode === 'voice' ? (
          <div className="py-1" data-testid="rating-voice-mode">
            <Suspense fallback={
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            }>
              <VoiceRecapRecorder
                booking={{ id: bookingId, booking_id: bookingId }}
                onConfirmed={() => {
                  toast.success('Voice recap submitted — thanks!');
                  reset();
                  onRatingSubmitted?.();
                  onClose?.();
                }}
                onCancel={() => setMode('typed')}
              />
            </Suspense>
          </div>
        ) : (
        <div className="space-y-5 py-2">
          {/* Gig context */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="font-medium text-sm">{booking?.role_name || booking?.company_name || 'Assignment'}</p>
            {booking?.venue_name && <p className="text-xs text-gray-600">{booking.venue_name}</p>}
            {booking?.shift_date && (
              <p className="text-xs text-gray-500">
                {formatDate(booking.shift_date)}
              </p>
            )}
          </div>

          {/* Overall — first, prominent */}
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <StarRow
              label={isClientRatingTalent ? 'Overall Performance *' : 'Overall Experience *'}
              value={overall}
              onChange={(v) => setScore(overallKey, v)}
              testId="rating-overall"
            />
          </div>

          {/* Per-category rows */}
          <div className="bg-white rounded-lg border border-gray-200 px-3">
            {dims.filter(([k]) => k !== overallKey).map(([key, label]) => (
              <StarRow
                key={key}
                label={label}
                value={scores[key] || 0}
                onChange={(v) => setScore(key, v)}
                testId={`rating-${key}`}
              />
            ))}
          </div>

          {/* Public feedback */}
          <div className="space-y-1">
            {/* Iter-235e — inline Voice Recap nudge.
                Shown only to talent (talent_to_client direction), only
                while they have not yet completed a voice recap. Self
                removes after the first confirmed voice recap (the
                /voice/status endpoint returns has_completed_voice=true). */}
            {wantsBanner && bannerEligible && (
              <button
                type="button"
                onClick={() => setMode('voice')}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-cyan-200 bg-gradient-to-r from-cyan-50 to-sky-50 px-3 py-2.5 mb-2 text-left hover:border-cyan-400 hover:from-cyan-100 transition-colors"
                data-testid="voice-recap-banner"
              >
                <span className="flex items-center gap-2">
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-cyan-600 text-white shrink-0">
                    <Mic className="w-4 h-4" />
                    <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-amber-300" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-cyan-900">
                      Try AI Voice Recap — faster than typing
                    </span>
                    <span className="text-[11px] text-cyan-700/80">
                      Hold the mic for 30-60 seconds. We&apos;ll draft the rest.
                    </span>
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-cyan-600 shrink-0" />
              </button>
            )}
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              {isClientRatingTalent ? 'Public Feedback (visible to talent)' : 'Public Feedback (visible to client)'}
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={isClientRatingTalent
                ? 'Share your experience — what worked, what could improve…'
                : 'Share your experience — what worked, what could improve…'}
              rows={3}
              className="resize-none"
              data-testid="rating-comment"
            />
          </div>

          {/* Client-only: private notes admin-only */}
          {isClientRatingTalent && (
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Private Notes (admin only — NOT shown to talent)
              </label>
              <Textarea
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                placeholder="Anything sensitive you want admins to know but not the talent…"
                rows={2}
                className="resize-none border-rose-200 focus:border-rose-400"
                data-testid="rating-private-notes"
              />
            </div>
          )}

          {/* Toggles */}
          <div className="space-y-3 bg-slate-50 rounded-lg p-3">
            {isClientRatingTalent ? (
              <>
                <label className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <RefreshCcw className="w-4 h-4 text-cyan-600" /> Would rehire
                  </span>
                  <Switch checked={rehire} onCheckedChange={setRehire} data-testid="rehire-toggle" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : 'text-rose-400'}`} />
                    Add to my favorites
                  </span>
                  <Switch checked={isFavorite} onCheckedChange={setIsFavorite} data-testid="favorite-toggle" />
                </label>
              </>
            ) : (
              <>
                <label className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <RefreshCcw className="w-4 h-4 text-cyan-600" /> Would work again
                  </span>
                  <Switch checked={wouldWorkAgain} onCheckedChange={setWouldWorkAgain} data-testid="would-work-again-toggle" />
                </label>
                <label className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <EyeOff className="w-4 h-4 text-slate-600" /> Anonymous to client
                  </span>
                  <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="anonymous-toggle" />
                </label>
                {anonymous && (
                  <p className="text-[11px] text-slate-500 leading-snug pl-6">
                    Your name + photo will be hidden from the client. Admins can still see your identity for moderation.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Reporting/abuse hint */}
          <div className="flex items-start gap-2 text-[11px] text-slate-500">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Reviews are moderated by the iEvntür admin team. Abusive, retaliatory, or off-topic
              feedback may be hidden or removed. Provide specific, factual notes to help the
              other side improve.
            </span>
          </div>
        </div>
        )}

        {mode !== 'voice' && (
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting} data-testid="rating-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || overall === 0 || filledCount < 3}
            className="bg-cyan-500 hover:bg-cyan-600"
            data-testid="submit-rating-btn"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              : <><Send className="w-4 h-4 mr-2" /> Submit Rating</>}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RatingFlow;
